import { useState } from 'react'
import { Calendar, Clock, CheckCircle2, Droplets, Car, ChevronRight, RotateCcw } from 'lucide-react'
import { cn } from '../lib/utils'
import { packages, timeSlots, vehicles } from '../data/carWashData'

const today = new Date().toISOString().split('T')[0]

export function CarWashDashboard() {
  const [step, setStep] = useState(1)
  const [selection, setSelection] = useState({
    pkg: packages[1],
    vehicle: vehicles[0],
    date: today,
    slot: timeSlots[4],
    plate: '',
    notes: '',
  })
  const [confirmed, setConfirmed] = useState(false)

  const total = Math.round(selection.pkg.price * selection.vehicle.multiplier)

  const steps = [
    { id: 1, label: 'Package' },
    { id: 2, label: 'Vehicle' },
    { id: 3, label: 'Schedule' },
    { id: 4, label: 'Confirm' },
  ]

  const handleConfirm = () => {
    setConfirmed(true)
  }

  const reset = () => {
    setSelection({
      pkg: packages[1],
      vehicle: vehicles[0],
      date: today,
      slot: timeSlots[4],
      plate: '',
      notes: '',
    })
    setConfirmed(false)
    setStep(1)
  }

  if (confirmed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-8 w-8 text-emerald-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Booking confirmed!</h2>
          <p className="mt-2 text-slate-600">
            {selection.pkg.name} for a {selection.vehicle.label} on{' '}
            <span className="font-medium text-slate-900">{selection.date}</span> at{' '}
            <span className="font-medium text-slate-900">{selection.slot}</span>.
          </p>
          <div className="mt-6 inline-block rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm">
            Total: ₱{total.toLocaleString()}
          </div>
          <div className="mt-8">
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <RotateCcw className="h-4 w-4" />
              Book another
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Book a Car Wash</h1>
          <p className="text-sm text-slate-500">Pick a package, vehicle, and time slot.</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-8 flex items-center gap-2 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <button
              onClick={() => setStep(s.id)}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors',
                step >= s.id ? 'bg-tms-600 text-white' : 'bg-slate-200 text-slate-600'
              )}
            >
              {s.id}
            </button>
            <span
              className={cn(
                'whitespace-nowrap text-sm font-medium',
                step >= s.id ? 'text-slate-900' : 'text-slate-500'
              )}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <ChevronRight className="h-4 w-4 text-slate-300" />
            )}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {step === 1 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Select package</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => setSelection((sel) => ({ ...sel, pkg }))}
                    className={cn(
                      'relative rounded-xl border p-5 text-left shadow-sm transition-all',
                      selection.pkg.id === pkg.id
                        ? 'border-tms-600 bg-tms-50 ring-1 ring-tms-600'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    <div className={cn('mb-3 inline-flex rounded-lg p-2 text-white', pkg.color)}>
                      <Droplets className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-slate-900">{pkg.name}</h3>
                    <p className="mt-1 text-sm text-slate-500">{pkg.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-lg font-bold text-slate-900">₱{pkg.price.toLocaleString()}</span>
                      <span className="text-xs text-slate-500">{pkg.duration}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-slate-900">Vehicle details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {vehicles.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelection((sel) => ({ ...sel, vehicle: v }))}
                    className={cn(
                      'flex items-center gap-4 rounded-xl border p-4 text-left shadow-sm transition-all',
                      selection.vehicle.id === v.id
                        ? 'border-tms-600 bg-tms-50 ring-1 ring-tms-600'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    <div className="rounded-lg bg-slate-100 p-2 text-slate-600">
                      <Car className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{v.label}</h3>
                      <p className="text-sm text-slate-500">
                        {v.multiplier === 1 ? 'Base price' : `+${Math.round((v.multiplier - 1) * 100)}% surcharge`}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700">License plate (optional)</label>
                <input
                  type="text"
                  value={selection.plate}
                  onChange={(e) => setSelection((sel) => ({ ...sel, plate: e.target.value.toUpperCase() }))}
                  placeholder="ABC-1234"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-tms-600 focus:outline-none focus:ring-2 focus:ring-tms-600/20"
                />
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Pick a date</h2>
                <div className="mt-3 flex items-center gap-3">
                  <div className="rounded-lg bg-slate-100 p-2 text-slate-600">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <input
                    type="date"
                    value={selection.date}
                    min={today}
                    onChange={(e) => setSelection((sel) => ({ ...sel, date: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-tms-600 focus:outline-none focus:ring-2 focus:ring-tms-600/20"
                  />
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-slate-900">Available time slots</h2>
                <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setSelection((sel) => ({ ...sel, slot }))}
                      className={cn(
                        'flex items-center justify-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                        selection.slot === slot
                          ? 'border-tms-600 bg-tms-600 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                      )}
                    >
                      <Clock className="h-3.5 w-3.5" />
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Special requests (optional)</label>
                <textarea
                  value={selection.notes}
                  onChange={(e) => setSelection((sel) => ({ ...sel, notes: e.target.value }))}
                  rows={3}
                  placeholder="e.g. focus on interior stains, pet hair removal"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-tms-600 focus:outline-none focus:ring-2 focus:ring-tms-600/20"
                />
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Review booking</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Package</dt>
                  <dd className="font-medium text-slate-900">{selection.pkg.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Vehicle</dt>
                  <dd className="font-medium text-slate-900">{selection.vehicle.label}</dd>
                </div>
                {selection.plate && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Plate</dt>
                    <dd className="font-medium text-slate-900">{selection.plate}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-500">Date &amp; time</dt>
                  <dd className="font-medium text-slate-900">{selection.date} at {selection.slot}</dd>
                </div>
                {selection.notes && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Requests</dt>
                    <dd className="max-w-xs text-right text-slate-900">{selection.notes}</dd>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3">
                  <div className="flex justify-between text-base font-bold text-slate-900">
                    <dt>Total</dt>
                    <dd>₱{total.toLocaleString()}</dd>
                  </div>
                </div>
              </dl>
              <button
                onClick={handleConfirm}
                className="mt-6 w-full rounded-lg bg-tms-600 py-2.5 text-sm font-semibold text-white hover:bg-tms-700"
              >
                Confirm booking
              </button>
            </section>
          )}

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className={cn(
                'rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700',
                step === 1 ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-50'
              )}
            >
              Back
            </button>
            {step < 4 && (
              <button
                onClick={() => setStep((s) => Math.min(4, s + 1))}
                className="flex items-center gap-2 rounded-lg bg-tms-600 px-4 py-2 text-sm font-medium text-white hover:bg-tms-700"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Summary sidebar */}
        <aside className="h-fit rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-900">Booking summary</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Package</span>
              <span className="font-medium text-slate-900">{selection.pkg.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Vehicle</span>
              <span className="font-medium text-slate-900">{selection.vehicle.label}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Date</span>
              <span className="font-medium text-slate-900">{selection.date}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Time</span>
              <span className="font-medium text-slate-900">{selection.slot}</span>
            </div>
          </div>
          <div className="mt-5 border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between text-base font-bold text-slate-900">
              <span>Total</span>
              <span>₱{total.toLocaleString()}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
